import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { formatApiError, API_BASE } from '../../api';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, FileDown, ExternalLink, FileText, Receipt, Zap, Pencil, Save, X, Repeat } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';

const STATUS_ORDER = ['draft', 'active', 'due_for_renewal', 'renewed', 'cancelled', 'expired'];
const STATUS_LABEL = { draft: 'Draft', active: 'Active', due_for_renewal: 'Due for Renewal', renewed: 'Renewed', cancelled: 'Cancelled', expired: 'Expired' };
const STATUS_TONE = {
    draft: 'bg-slate-100 text-slate-700 border-slate-200',
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    due_for_renewal: 'bg-amber-50 text-amber-800 border-amber-200',
    renewed: 'bg-sky-50 text-sky-700 border-sky-200',
    cancelled: 'bg-red-50 text-red-700 border-red-200',
    expired: 'bg-slate-100 text-slate-600 border-slate-200',
};
const GBP = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }).format(Number(v || 0));
const FREQ = { monthly: 'Monthly', quarterly: 'Quarterly', annually: 'Annually', annual_upfront: 'Annual upfront' };

export default function ContractDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [contract, setContract] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [customOpen, setCustomOpen] = useState(false);
    const [dupWarning, setDupWarning] = useState(null);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({});
    const [savingEdit, setSavingEdit] = useState(false);

    const load = useCallback(async () => {
        try {
            const [c, inv] = await Promise.all([
                api.get(`/contracts/${id}`),
                api.get('/invoices', { params: { contract_id: id } }),
            ]);
            setContract(c.data);
            setInvoices(inv.data.items);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
            if (e?.response?.status === 404) navigate('/contracts');
        }
    }, [id, navigate]);

    useEffect(() => { load(); }, [load]);

    const setStatus = async (status) => {
        try {
            const r = await api.patch(`/contracts/${id}`, { status });
            setContract(r.data);
            toast.success(`Status updated to ${STATUS_LABEL[status]}`);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const startEdit = () => {
        setDraft({
            start_date: contract.start_date || '',
            end_date: contract.end_date || '',
            renewal_date: contract.renewal_date || '',
            contract_term_months: contract.contract_term_months ?? '',
            payment_frequency: contract.payment_frequency || '',
            setup_fee: contract.setup_fee ?? '',
            deposit_paid_amount: contract.deposit_paid_amount ?? '',
            monthly_saas_fee: contract.monthly_saas_fee ?? '',
            annual_saas_fee: contract.annual_saas_fee ?? '',
            vat_amount: contract.vat_amount ?? '',
            total_inc_vat_year_one: contract.total_inc_vat_year_one ?? '',
            agreement_terms_snapshot: contract.agreement_terms_snapshot || '',
            signed_by_name: contract.signed_by_name || '',
            signed_by_title: contract.signed_by_title || '',
            signed_by_email: contract.signed_by_email || '',
            signed_by_company: contract.signed_by_company || '',
        });
        setEditing(true);
    };

    const saveEdit = async () => {
        setSavingEdit(true);
        try {
            const numKeys = ['contract_term_months', 'setup_fee', 'deposit_paid_amount', 'monthly_saas_fee', 'annual_saas_fee', 'vat_amount', 'total_inc_vat_year_one'];
            const body = {};
            Object.entries(draft).forEach(([k, v]) => {
                if (v === '' || v == null) return;
                body[k] = numKeys.includes(k) ? Number(v) : v;
            });
            const r = await api.patch(`/contracts/${id}`, body);
            setContract(r.data);
            setEditing(false);
            toast.success('Contract updated');
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setSavingEdit(false); }
    };
    const dField = (k, v) => setDraft(d => ({ ...d, [k]: v }));

    const createInvoice = async (kind, opts = {}) => {
        try {
            const r = await api.post(`/invoices/from-contract/${id}`, { kind, force: opts.force || false, ...opts });
            if (r.data.duplicate) {
                setDupWarning({ kind, ...r.data });
                return;
            }
            if (r.data.skipped) { toast.message(r.data.message); return; }
            toast.success(`Created ${r.data.invoice.invoice_number}`);
            navigate(`/invoices/${r.data.invoice.id}`);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    if (!contract) return <div className="p-8 text-[color:var(--bm-text-muted)]">Loading…</div>;
    const opt = contract.accepted_option_snapshot || {};
    const mods = contract.modules_snapshot || [];

    return (
        <div data-testid="contract-detail-page" className="bm-fade-in">
            <PageHeader
                title={contract.contract_number}
                description={`${contract.client_name} · from ${contract.proposal_number || '—'}`}
                actions={
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={() => navigate('/contracts')} className="h-9" data-testid="contract-back-btn">
                            <ArrowLeft size={15} className="mr-1" /> Back
                        </Button>
                        {!editing && (
                            <Button variant="outline" onClick={startEdit} className="h-9" data-testid="contract-edit-btn">
                                <Pencil size={15} className="mr-1" /> Edit
                            </Button>
                        )}
                        {editing && (
                            <>
                                <Button variant="outline" onClick={() => setEditing(false)} className="h-9" data-testid="contract-cancel-edit-btn">
                                    <X size={15} className="mr-1" /> Cancel
                                </Button>
                                <Button onClick={saveEdit} disabled={savingEdit} className="h-9" style={{ background: 'var(--bm-primary)' }} data-testid="contract-save-edit-btn">
                                    <Save size={15} className="mr-1" /> {savingEdit ? 'Saving…' : 'Save'}
                                </Button>
                            </>
                        )}
                        <Button variant="outline" onClick={() => navigate(`/recurring/new?client_id=${contract.client_id}&contract_id=${id}`)} className="h-9" data-testid="contract-recurring-btn">
                            <Repeat size={15} className="mr-1" /> Recurring billing
                        </Button>
                        <Button variant="outline" onClick={() => window.open(`${API_BASE}/contracts/${id}/pdf`, '_blank')} className="h-9" data-testid="contract-pdf-btn">
                            <FileDown size={15} className="mr-1" /> Signed PDF
                        </Button>
                        {contract.proposal_id && (
                            <Button variant="outline" onClick={() => navigate(`/proposals/${contract.proposal_id}`)} className="h-9" data-testid="contract-open-proposal-btn">
                                <ExternalLink size={15} className="mr-1" /> Open proposal
                            </Button>
                        )}
                    </div>
                }
            >
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <span className={`bm-pill ${STATUS_TONE[contract.status]} border`}>{STATUS_LABEL[contract.status] || contract.status}</span>
                    <div className="ml-auto flex items-center gap-2">
                        <span className="text-[12px] text-[color:var(--bm-text-muted)]">Change status</span>
                        <Select value={contract.status} onValueChange={setStatus}>
                            <SelectTrigger className="h-8 w-[160px] bg-white" data-testid="contract-status-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {STATUS_ORDER.map(s => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </PageHeader>

            <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 space-y-5">
                    {/* One-tap invoice actions */}
                    <Card title="Invoicing" actions={
                        <span className="text-[11.5px] text-[color:var(--bm-text-subtle)]">One-tap from this contract</span>
                    }>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <Button variant="outline" onClick={() => createInvoice('setup')} className="h-10 justify-start" data-testid="invoice-setup-btn">
                                <Zap size={14} className="mr-1.5 text-[color:var(--bm-accent-600)]" /> Setup invoice
                            </Button>
                            <Button variant="outline" onClick={() => createInvoice('saas_monthly')} className="h-10 justify-start" data-testid="invoice-saas-monthly-btn">
                                <Zap size={14} className="mr-1.5 text-[color:var(--bm-accent-600)]" /> Month-1 SaaS
                            </Button>
                            <Button variant="outline" onClick={() => createInvoice('saas_annual')} className="h-10 justify-start" data-testid="invoice-saas-annual-btn">
                                <Zap size={14} className="mr-1.5 text-[color:var(--bm-accent-600)]" /> Annual SaaS
                            </Button>
                            <Button variant="outline" onClick={() => setCustomOpen(true)} className="h-10 justify-start" data-testid="invoice-custom-btn">
                                <Receipt size={14} className="mr-1.5 text-[color:var(--bm-primary)]" /> Custom invoice
                            </Button>
                        </div>
                        {invoices.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-[color:var(--bm-border)]">
                                <div className="text-[11px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold mb-2">Existing invoices ({invoices.length})</div>
                                <div className="space-y-1">
                                    {invoices.map(i => (
                                        <button key={i.id} onClick={() => navigate(`/invoices/${i.id}`)}
                                                className="w-full flex items-center justify-between text-[12.5px] py-1.5 px-2 rounded hover:bg-[color:var(--bm-bg-mute)]"
                                                data-testid={`contract-invoice-${i.id}`}>
                                            <span><span className="font-medium">{i.invoice_number}</span> · <span className="text-[color:var(--bm-text-muted)]">{i.type.replace('_', ' ')}</span></span>
                                            <span className="text-[color:var(--bm-text-muted)]">{GBP(i.total)} · {i.status}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </Card>

                    <Card title="Commercial summary">
                        {editing ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="contract-edit-commercial">
                                <EF label="Start date" type="date" value={draft.start_date} onChange={v => dField('start_date', v)} />
                                <EF label="End date" type="date" value={draft.end_date} onChange={v => dField('end_date', v)} />
                                <EF label="Renewal date" type="date" value={draft.renewal_date} onChange={v => dField('renewal_date', v)} />
                                <EF label="Contract term (months)" type="number" value={draft.contract_term_months} onChange={v => dField('contract_term_months', v)} />
                                <div>
                                    <div className="text-[10.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold mb-1">Payment frequency</div>
                                    <Select value={draft.payment_frequency || ''} onValueChange={v => dField('payment_frequency', v)}>
                                        <SelectTrigger className="h-9" data-testid="contract-edit-frequency"><SelectValue placeholder="Select" /></SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(FREQ).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <EF label="Setup total (£)" type="number" value={draft.setup_fee} onChange={v => dField('setup_fee', v)} />
                                <EF label="Deposit paid (£)" type="number" value={draft.deposit_paid_amount} onChange={v => dField('deposit_paid_amount', v)} />
                                <EF label="Monthly total (£)" type="number" value={draft.monthly_saas_fee} onChange={v => dField('monthly_saas_fee', v)} />
                                <EF label="Annual recurring total (£)" type="number" value={draft.annual_saas_fee} onChange={v => dField('annual_saas_fee', v)} />
                                <EF label="VAT (£)" type="number" value={draft.vat_amount} onChange={v => dField('vat_amount', v)} />
                                <EF label="Year-one total incl VAT (£)" type="number" value={draft.total_inc_vat_year_one} onChange={v => dField('total_inc_vat_year_one', v)} />
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-[13px]">
                                <KV label="Selected option"    value={opt.name || '—'} />
                                <KV label="Start date"          value={contract.start_date} />
                                <KV label="End date"            value={contract.end_date} />
                                <KV label="Renewal date"        value={contract.renewal_date} accent />
                                <KV label="Contract term"       value={`${contract.contract_term_months} months`} />
                                <KV label="Payment frequency"   value={FREQ[contract.payment_frequency] || contract.payment_frequency} />
                                <KV label="Setup total"         value={GBP(contract.setup_fee)} />
                                <KV label="Deposit paid"        value={GBP(contract.deposit_paid_amount)} />
                                <KV label="Monthly total"       value={GBP(contract.monthly_saas_fee)} />
                                <KV label="Annual recurring total" value={GBP(contract.annual_saas_fee)} />
                                <KV label="VAT"                 value={GBP(contract.vat_amount)} />
                                <KV label="Year-one total including VAT" value={GBP(contract.total_inc_vat_year_one)} strong />
                            </div>
                        )}
                    </Card>

                    {mods.length > 0 && (
                        <Card title="Included modules (snapshot)">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {mods.map(m => (
                                    <div key={m.id} className="border border-[color:var(--bm-border)] rounded-lg p-3 bg-[color:var(--bm-bg-mute)]">
                                        <div className="text-[13px] font-semibold text-[color:var(--bm-text)]">{m.name}</div>
                                        {m.description && <div className="text-[11.5px] text-[color:var(--bm-text-muted)] mt-0.5">{m.description}</div>}
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}

                    {(contract.ad_hoc_modules_snapshot || []).length > 0 && (
                        <Card title="Additional items (snapshot)">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {contract.ad_hoc_modules_snapshot.map((m, i) => (
                                    <div key={m.id || i} className="border border-[color:var(--bm-border)] rounded-lg p-3 bg-[color:var(--bm-bg-mute)]" data-testid={`contract-adhoc-${i}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="text-[13px] font-semibold text-[color:var(--bm-text)]">{m.name}</div>
                                            {(m.suggested_setup_price > 0 || m.suggested_monthly_price > 0) && (
                                                <div className="text-[11px] text-[color:var(--bm-text-muted)] text-right">
                                                    {m.suggested_setup_price > 0 && <div>{GBP(m.suggested_setup_price)} setup</div>}
                                                    {m.suggested_monthly_price > 0 && <div>{GBP(m.suggested_monthly_price)}/mo</div>}
                                                </div>
                                            )}
                                        </div>
                                        {m.description && <div className="text-[11.5px] text-[color:var(--bm-text-muted)] mt-0.5">{m.description}</div>}
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}

                    {contract.hosting_snapshot && (
                        <Card title="Hosting & Access (snapshot)">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-[13px]" data-testid="contract-hosting-snapshot">
                                <KV label="Option" value={contract.hosting_snapshot.label || contract.hosting_snapshot.key || '—'} />
                                <KV label="Example / agreed URL" value={contract.hosting_snapshot.example_url || '—'} />
                                {(contract.hosting_snapshot.setup_fee > 0 || contract.hosting_snapshot.monthly_support_fee > 0) && (
                                    <>
                                        <KV label="Setup fee" value={GBP(contract.hosting_snapshot.setup_fee)} />
                                        <KV label="Monthly support" value={`${GBP(contract.hosting_snapshot.monthly_support_fee)}/mo`} />
                                    </>
                                )}
                            </div>
                            {contract.hosting_snapshot.description && (
                                <div className="mt-3 text-[12px] text-[color:var(--bm-text-muted)]">{contract.hosting_snapshot.description}</div>
                            )}
                            {contract.hosting_snapshot.notes && (
                                <div className="mt-2 text-[12px] text-[color:var(--bm-text-muted)]"><span className="font-semibold">Notes:</span> {contract.hosting_snapshot.notes}</div>
                            )}
                        </Card>
                    )}

                    <Card title="Agreement terms (snapshot)">
                        {editing ? (
                            <Textarea value={draft.agreement_terms_snapshot} onChange={e => dField('agreement_terms_snapshot', e.target.value)}
                                      rows={12} className="text-[12.5px]" data-testid="contract-edit-terms" />
                        ) : (
                            <pre className="whitespace-pre-wrap text-[12.5px] text-[color:var(--bm-text-muted)] leading-relaxed max-h-[320px] overflow-y-auto">
                                {contract.agreement_terms_snapshot || '—'}
                            </pre>
                        )}
                    </Card>
                </div>

                <div className="space-y-5">
                    <Card title="Signatory">
                        {editing ? (
                            <div className="space-y-3" data-testid="contract-edit-signatory">
                                <EF label="Name" value={draft.signed_by_name} onChange={v => dField('signed_by_name', v)} />
                                <EF label="Title" value={draft.signed_by_title} onChange={v => dField('signed_by_title', v)} />
                                <EF label="Email" value={draft.signed_by_email} onChange={v => dField('signed_by_email', v)} />
                                <EF label="Company" value={draft.signed_by_company} onChange={v => dField('signed_by_company', v)} />
                            </div>
                        ) : (
                            <>
                                <div className="space-y-2 text-[13px]">
                                    <KV label="Name"    value={contract.signed_by_name} />
                                    <KV label="Title"   value={contract.signed_by_title || '—'} />
                                    <KV label="Email"   value={contract.signed_by_email} />
                                    {contract.signed_by_company && <KV label="Company" value={contract.signed_by_company} />}
                                    <KV label="Signed at" value={contract.signed_at ? new Date(contract.signed_at).toLocaleString('en-GB') : '—'} />
                                </div>
                                {contract.signature_image_base64 && (
                                    <div className="mt-4">
                                        <div className="text-[10.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold mb-1.5">Drawn signature</div>
                                        <div className="border border-[color:var(--bm-border)] rounded-md bg-white p-2">
                                            <img src={contract.signature_image_base64.startsWith('data:') ? contract.signature_image_base64 : `data:image/png;base64,${contract.signature_image_base64}`}
                                                 alt="Signature" className="max-h-[80px]" data-testid="contract-signature-img" />
                                        </div>
                                    </div>
                                )}
                                {contract.typed_signature && (
                                    <div className="mt-3">
                                        <div className="text-[10.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold mb-1.5">Typed signature</div>
                                        <div className="font-display italic text-[16px] text-[color:var(--bm-primary)]">{contract.typed_signature}</div>
                                    </div>
                                )}
                            </>
                        )}
                    </Card>

                    <Card title="Audit">
                        <div className="space-y-2 text-[12px] text-[color:var(--bm-text-muted)]">
                            {contract.signature_ip && <div><span className="text-[color:var(--bm-text-subtle)]">IP:</span> {contract.signature_ip}</div>}
                            {contract.signature_user_agent && <div className="break-all"><span className="text-[color:var(--bm-text-subtle)]">User agent:</span> {contract.signature_user_agent}</div>}
                            <div><span className="text-[color:var(--bm-text-subtle)]">Created:</span> {new Date(contract.created_at).toLocaleString('en-GB')}</div>
                        </div>
                    </Card>
                </div>
            </div>
            <CustomInvoiceDialog open={customOpen} onClose={() => setCustomOpen(false)} onCreate={(k, o) => { setCustomOpen(false); createInvoice(k, o); }} />
            <DuplicateDialog
                dup={dupWarning}
                onClose={() => setDupWarning(null)}
                onView={() => { const id = dupWarning.existing_invoice_id; setDupWarning(null); navigate(`/invoices/${id}`); }}
                onForce={() => { const k = dupWarning.kind; setDupWarning(null); createInvoice(k, { force: true }); }}
            />
        </div>
    );
}

function Card({ title, children, actions }) {
    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="font-display text-[15px] font-semibold">{title}</div>
                {actions}
            </div>
            {children}
        </div>
    );
}

function KV({ label, value, strong, accent }) {
    return (
        <div>
            <div className="text-[10.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">{label}</div>
            <div className={`mt-0.5 ${strong ? 'font-display text-[17px] font-semibold' : 'font-medium'} ${accent ? 'text-[color:var(--bm-accent-600)]' : 'text-[color:var(--bm-text)]'}`}>
                {value}
            </div>
        </div>
    );
}

function EF({ label, value, onChange, type = 'text' }) {
    return (
        <div>
            <div className="text-[10.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold mb-1">{label}</div>
            <Input type={type} step={type === 'number' ? '0.01' : undefined} value={value ?? ''} onChange={e => onChange(e.target.value)} className="h-9 text-[13px]" />
        </div>
    );
}

/* ── inline tiny helpers for dialogs ─────────────────────────────────────── */
function CustomInvoiceDialog({ open, onClose, onCreate }) {
    const [desc, setDesc] = useState('');
    const [amt, setAmt] = useState('');
    const submit = (e) => {
        e.preventDefault();
        if (!desc.trim()) return toast.error('Description required');
        const n = Number(amt);
        if (!n || n <= 0) return toast.error('Amount must be > 0');
        onCreate('custom', { custom_description: desc.trim(), custom_amount: n });
        setDesc(''); setAmt('');
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-[440px]">
                <DialogHeader>
                    <DialogTitle>Custom invoice</DialogTitle>
                    <DialogDescription className="text-[12.5px] text-[color:var(--bm-text-muted)]">One-off invoice raised against this contract.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3 mt-2">
                    <div>
                        <label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Description *</label>
                        <input value={desc} onChange={e => setDesc(e.target.value)} className="mt-1.5 w-full h-10 rounded-md border border-[color:var(--bm-border)] px-3 text-[13px]" />
                    </div>
                    <div>
                        <label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Amount (£) *</label>
                        <input type="number" step="0.01" value={amt} onChange={e => setAmt(e.target.value)} className="mt-1.5 w-full h-10 rounded-md border border-[color:var(--bm-border)] px-3 text-[13px]" />
                    </div>
                    <DialogFooter className="pt-2">
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" style={{ background: 'var(--bm-primary)' }}>Create</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function DuplicateDialog({ dup, onClose, onForce, onView }) {
    if (!dup) return null;
    return (
        <Dialog open={!!dup} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-[440px]">
                <DialogHeader>
                    <DialogTitle>Possible duplicate invoice</DialogTitle>
                    <DialogDescription className="text-[12.5px] text-[color:var(--bm-text-muted)]">An invoice of this kind already exists for this contract.</DialogDescription>
                </DialogHeader>
                <div className="mt-2 p-3 rounded-md bg-[color:var(--bm-bg-mute)] text-[13px]">
                    <div><span className="text-[color:var(--bm-text-muted)]">Existing:</span> <span className="font-semibold">{dup.existing_invoice_number}</span></div>
                    <div><span className="text-[color:var(--bm-text-muted)]">Status:</span> {dup.existing_status}</div>
                </div>
                <DialogFooter className="pt-3">
                    <Button type="button" variant="outline" onClick={onClose}>Close</Button>
                    <Button type="button" variant="outline" onClick={onView}>View existing</Button>
                    <Button type="button" onClick={onForce} style={{ background: 'var(--bm-primary)' }}>Create anyway</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
/* ──────────────────────────────────────────────────────────────────────────── */
